import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

export const ThemeContext = createContext({
  mode: 'light',
  toggleTheme: () => {},
  isDark: false
});

export const useThemeMode = () => useContext(ThemeContext);

export const getAppTheme = (mode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#38BDF8' : '#0F172A',
        light: isDark ? '#7DD3FC' : '#1E293B',
        dark: isDark ? '#0284C7' : '#020617',
        contrastText: isDark ? '#0B0F19' : '#FFFFFF'
      },
      secondary: {
        main: isDark ? '#818CF8' : '#4F46E5',
        light: '#A5B4FC',
        dark: '#3730A3'
      },
      background: {
        default: isDark ? '#0B0F19' : '#F8FAFC',
        paper: isDark ? '#111827' : '#FFFFFF',
      },
      text: {
        primary: isDark ? '#F9FAFB' : '#0F172A',
        secondary: isDark ? '#9CA3AF' : '#64748B',
        disabled: isDark ? '#4B5563' : '#94A3B8'
      },
      divider: isDark ? '#1F2937' : '#E2E8F0',
      action: {
        hover: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
        selected: isDark ? 'rgba(56, 189, 248, 0.12)' : '#EEF2FF',
      },
      success: {
        main: isDark ? '#34D399' : '#10B981',
        light: isDark ? 'rgba(52, 211, 153, 0.16)' : '#D1FAE5',
        dark: '#059669',
        contrastText: '#FFFFFF'
      },
      error: {
        main: isDark ? '#F87171' : '#EF4444',
        light: isDark ? 'rgba(248, 113, 113, 0.16)' : '#FEE2E2',
        dark: '#DC2626',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#F59E0B',
        light: isDark ? 'rgba(245, 158, 11, 0.16)' : '#FEF3C7',
        dark: '#D97706'
      },
      info: {
        main: '#38BDF8',
        light: isDark ? 'rgba(56, 189, 248, 0.16)' : '#E0F2FE',
        dark: '#0284C7'
      },
      grey: isDark ? {
        50: '#0F172A',
        100: '#1E293B',
        200: '#334155',
        300: '#475569',
        400: '#64748B',
        500: '#94A3B8',
        600: '#CBD5E1',
        700: '#334155',
        800: '#1E293B',
        900: '#0F172A'
      } : {
        50: '#F8FAFC',
        100: '#F1F5F9',
        200: '#E2E8F0',
        300: '#CBD5E1',
        400: '#94A3B8',
        500: '#64748B',
        600: '#475569',
        700: '#334155',
        800: '#1E293B',
        900: '#0F172A'
      }
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.5px'
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.3px'
      },
      h6: {
        fontWeight: 600,
      }
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            colorScheme: isDark ? 'dark' : 'light'
          },
          body: {
            backgroundColor: isDark ? '#0B0F19' : '#F8FAFC',
            color: isDark ? '#F9FAFB' : '#0F172A',
            transition: 'background-color 0.2s ease, color 0.2s ease'
          },
          '.MuiTooltip-popper': {
            zIndex: '2000 !important'
          },
          '.MuiTooltip-tooltip': {
            backgroundColor: isDark ? '#1E293B !important' : '#0F172A !important',
            color: '#FFFFFF !important',
            border: isDark ? '1px solid #334155 !important' : '1px solid rgba(255, 255, 255, 0.15) !important',
            borderRadius: '8px !important'
          },
          '.MuiTooltip-arrow': {
            color: isDark ? '#1E293B !important' : '#0F172A !important'
          },
          '.recharts-default-tooltip': {
            backgroundColor: isDark ? '#1E293B !important' : '#FFFFFF !important',
            borderColor: isDark ? '#334155 !important' : '#E2E8F0 !important',
            color: isDark ? '#F9FAFB !important' : '#0F172A !important',
            boxShadow: isDark
              ? '0 10px 25px -5px rgba(0, 0, 0, 0.6) !important'
              : '0 10px 15px -3px rgba(0, 0, 0, 0.1) !important',
            borderRadius: '8px !important',
            padding: '8px 12px !important'
          },
          '.recharts-tooltip-item': {
            color: isDark ? '#F9FAFB !important' : '#0F172A !important'
          },
          '.recharts-tooltip-label': {
            color: isDark ? '#F9FAFB !important' : '#0F172A !important',
            fontWeight: '600 !important'
          },
          '.recharts-tooltip-cursor': {
            fill: isDark ? 'rgba(255, 255, 255, 0.06) !important' : 'rgba(0, 0, 0, 0.05) !important'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: isDark
                ? '0 4px 10px rgba(0, 0, 0, 0.4)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#111827' : '#FFFFFF',
            boxShadow: isDark
              ? '0 4px 14px rgba(0, 0, 0, 0.35)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
            border: isDark ? '1px solid #1F2937' : '1px solid #E2E8F0',
            color: isDark ? '#F9FAFB' : '#0F172A'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isDark ? '#111827' : '#FFFFFF',
            border: isDark ? '1px solid #1F2937' : undefined,
            color: isDark ? '#F9FAFB' : '#0F172A'
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1E293B' : '#F8FAFC',
            '& .MuiTableRow-root': {
              backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important',
              '&:hover': {
                backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important'
              }
            },
            '& .MuiTableCell-root': {
              backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important',
              color: isDark ? '#94A3B8 !important' : '#475569 !important',
              fontWeight: 700
            },
            '& .MuiTableSortLabel-root': {
              color: isDark ? '#94A3B8 !important' : '#475569 !important',
              '&:hover': {
                color: isDark ? '#F9FAFB !important' : '#0F172A !important'
              },
              '&.Mui-active': {
                color: isDark ? '#38BDF8 !important' : '#0F172A !important',
                '& .MuiTableSortLabel-icon': {
                  color: isDark ? '#38BDF8 !important' : '#0F172A !important'
                }
              }
            }
          }
        }
      },
      MuiTableFooter: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1E293B' : '#F8FAFC',
            '& .MuiTableRow-root': {
              backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important',
              '&:hover': {
                backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important'
              }
            },
            '& .MuiTableCell-root': {
              backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important',
              color: isDark ? '#F9FAFB !important' : '#0F172A !important'
            }
          }
        }
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#111827' : '#FFFFFF',
            color: isDark ? '#94A3B8' : '#475569',
            borderTop: isDark ? '1px solid #1F2937' : '1px solid #E2E8F0',
            '&:hover': {
              backgroundColor: isDark ? '#111827 !important' : '#FFFFFF !important'
            },
            '& .MuiTablePagination-toolbar': {
              '&:hover': {
                backgroundColor: isDark ? '#111827 !important' : '#FFFFFF !important'
              }
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              color: isDark ? '#94A3B8' : '#475569'
            },
            '& .MuiTablePagination-select': {
              color: isDark ? '#F9FAFB' : '#0F172A'
            },
            '& .MuiIconButton-root': {
              color: isDark ? '#94A3B8' : '#64748B',
              '&.Mui-disabled': {
                color: isDark ? '#4B5563' : '#CBD5E1'
              }
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isDark ? '#1F2937' : '#E2E8F0',
            color: isDark ? '#E5E7EB' : '#0F172A'
          },
          head: {
            backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important',
            color: isDark ? '#94A3B8 !important' : '#475569 !important',
            fontWeight: 700
          },
          footer: {
            backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important',
            color: isDark ? '#F9FAFB !important' : '#0F172A !important'
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover, tbody tr:hover': {
              backgroundColor: isDark ? '#1F293D !important' : '#F8FAFC !important'
            },
            'thead &:hover, tfoot &:hover, &.MuiTableRow-head:hover, &.MuiTableRow-footer:hover': {
              backgroundColor: isDark ? '#1E293B !important' : '#F8FAFC !important'
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#111827' : '#FFFFFF',
            color: isDark ? '#F9FAFB' : '#0F172A',
            '& .MuiSvgIcon-root': {
              color: isDark ? '#94A3B8' : '#64748B'
            }
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: isDark ? '#F9FAFB' : '#0F172A',
            '&:hover': {
              backgroundColor: isDark ? '#1E293B' : '#F1F5F9'
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(56, 189, 248, 0.16)' : '#EEF2FF',
              color: isDark ? '#38BDF8' : '#4F46E5',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(56, 189, 248, 0.24)' : '#E0E7FF'
              }
            }
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '4px 12px',
            padding: '8px 16px',
            transition: 'all 0.2s',
            color: isDark ? '#CBD5E1' : '#334155',
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(56, 189, 248, 0.14)' : '#EEF2FF',
              color: isDark ? '#38BDF8' : '#4F46E5',
              fontWeight: 700,
              '& .MuiListItemIcon-root': {
                color: isDark ? '#38BDF8' : '#4F46E5'
              },
              '&:hover': {
                backgroundColor: isDark ? 'rgba(56, 189, 248, 0.2)' : '#E0E7FF'
              }
            },
            '&:hover': {
              backgroundColor: isDark ? '#1E293B' : '#F1F5F9'
            }
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0B0F19' : '#FFFFFF',
            borderRight: isDark ? '1px solid #1F2937' : '1px solid #E2E8F0'
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: isDark ? '#F9FAFB' : '#0F172A',
            '& fieldset': {
              borderColor: isDark ? '#374151' : '#CBD5E1'
            },
            '&:hover fieldset': {
              borderColor: isDark ? '#60A5FA' : '#94A3B8'
            },
            '&.Mui-focused fieldset': {
              borderColor: isDark ? '#38BDF8' : '#4F46E5'
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#111827' : '#FFFFFF',
            border: isDark ? '1px solid #1F2937' : 'none'
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#111827' : '#FFFFFF',
            border: isDark ? '1px solid #1F2937' : 'none',
            color: isDark ? '#F9FAFB' : '#0F172A'
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#111827 !important' : '#FFFFFF !important',
            color: isDark ? '#F9FAFB !important' : '#0F172A !important',
            border: isDark ? '1px solid #1F2937' : undefined
          }
        }
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true
        },
        styleOverrides: {
          popper: {
            zIndex: 2000
          },
          tooltip: {
            backgroundColor: isDark ? '#1E293B !important' : '#0F172A !important',
            color: '#FFFFFF !important',
            border: isDark ? '1px solid #334155 !important' : '1px solid rgba(255, 255, 255, 0.15) !important',
            boxShadow: isDark
              ? '0 10px 25px -5px rgba(0, 0, 0, 0.8), 0 8px 10px -6px rgba(0, 0, 0, 0.7) !important'
              : '0 10px 20px -3px rgba(0, 0, 0, 0.3) !important',
            fontSize: '0.8rem',
            lineHeight: 1.4,
            borderRadius: '8px !important',
            padding: '8px 12px !important',
            '& .MuiTypography-root': {
              color: 'inherit'
            }
          },
          arrow: {
            color: isDark ? '#1E293B !important' : '#0F172A !important'
          }
        }
      }
    }
  });
};

export const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('psx_theme_mode') : null;
    const hasManualOverride = typeof window !== 'undefined' && localStorage.getItem('psx_theme_manual') === 'true';
    if (saved && hasManualOverride) {
      return saved;
    }
    return getSystemTheme();
  });

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('psx_theme_mode', next);
      localStorage.setItem('psx_theme_manual', 'true');
      return next;
    });
  };

  // Listen to OS / Browser color scheme preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e) => {
      const systemMode = e.matches ? 'dark' : 'light';
      setMode(systemMode);
      localStorage.setItem('psx_theme_mode', systemMode);
      localStorage.removeItem('psx_theme_manual');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleSystemThemeChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = mode === 'dark' ? '#0B0F19' : '#F8FAFC';
    document.documentElement.style.colorScheme = mode === 'dark' ? 'dark' : 'light';
  }, [mode]);

  const theme = useMemo(() => getAppTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, isDark: mode === 'dark' }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

